import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Stack,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  FormHelperText,
  Autocomplete,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Add, 
  Search, 
  FilterList, 
  ArrowBack,
  LocalOffer,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListDealsOfTheDayQuery,
  useAddDealMutation,
  useUpdateDealMutation,
  useDeleteDealOfTheDayMutation,
} from '../../features/admin/dealoftheday.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

// FIXED: Corrected interface to match backend response
interface DealOfTheDay {
  deal_id: number;
  product_id: string | number;
  status: number;  
  deal_status?: number;
  discount: number;
  start_date: string;
  end_date: string;
  date_status?: string; 
  final_status?: string; 
}

interface EnrichedDeal extends DealOfTheDay {
  product_name?: string;
  product_image?: string;
  prod_name?: string;
  product_main_image?: string;
  original_price?: number;
  currency?: string;
  sale_price?: number;
  vendor_id?: number | string | null;
  vendor_name?: string | null;
}

interface DealFormData {
  deal_id?: number;
  product_id: string;
  status: number;
  discount: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
}

const getVendorId = (item: any): number | string | null => {
  const value = item?.vendor_id ?? item?.vendorId ?? item?.seller_id ?? item?.sellerId;
  return value === undefined || value === null || value === '' ? null : value;
};

const getVendorName = (item: any): string | null => {
  const value = item?.vendor_name ?? item?.vendorName ?? item?.business_name ?? item?.vendor_business_name;
  return value ? String(value) : null;
};

const DealOfTheDay = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  // Admin filter: show Deals of the Day belonging to one vendor by name or ID.
  const [vendorSearchStr, setVendorSearchStr] = useState('');
  const [productVendorSearchStr, setProductVendorSearchStr] = useState('');
  const [productSearchStr, setProductSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error'
  });
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [enrichedDeals, setEnrichedDeals] = useState<EnrichedDeal[]>([]);
  const [filteredDeals, setFilteredDeals] = useState<EnrichedDeal[]>([]);
  const [formData, setFormData] = useState<DealFormData>({
    product_id: '',
    status: 1,
    discount: '',
    start_date: '',
    end_date: '',
    start_time: '00:00',
    end_time: '23:59',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof DealFormData, string>>>({});

  // Ref for scrolling
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListDealsOfTheDayQuery({});

  const {
    data: allProductsResponse,
    isLoading: isLoadingAllProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: ''
  });

  // Live search-as-you-type (mirrors Flash Sale's product picker) — searches
  // the backend by name/ID instead of only matching against the 1000
  // already-loaded products.
  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 50,
    search_str: productSearchStr
  });

  const [addDeal, { isLoading: isAdding }] = useAddDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();
  const [deleteDeal, { isLoading: isDeleting }] = useDeleteDealOfTheDayMutation();

  const isSaving = isAdding || isUpdating;
  const isEditing = Boolean(formData.deal_id);

  const deals = data?.data || [];
  const allProducts = allProductsResponse?.result || allProductsResponse?.data || [];
  const products = productsResponse?.result || productsResponse?.data || [];
  const vendorFilteredProducts = productVendorSearchStr.trim()
    ? allProducts.filter((product: Product) => {
        const query = productVendorSearchStr.trim().toLowerCase();
        const vendorName = getVendorName(product)?.toLowerCase() || '';
        const vendorId = getVendorId(product)?.toString().toLowerCase() || '';
        return vendorName.includes(query) || vendorId.includes(query);
      })
    : allProducts;

  // When vendor search is active, search the complete product list so every
  // product belonging to that vendor is available, not only the first 50
  // backend search results.
  const productOptions = productVendorSearchStr.trim()
    ? vendorFilteredProducts.filter((product: Product) => {
        const q = productSearchStr.trim().toLowerCase();
        if (!q) return true;
        return (product.prod_name || '').toLowerCase().includes(q) ||
          (product.product_id || product.id || '').toString().includes(q);
      })
    : products;

  const dealsPerPage = 10;
  const totalRecords = filteredDeals.length;
  const totalPages = Math.ceil(totalRecords / dealsPerPage);

  const paginatedDeals = filteredDeals.slice(
    (page - 1) * dealsPerPage,
    page * dealsPerPage
  );

  // Scroll handlers
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  /**
   * FIXED: Extract status value from backend response
   * Backend sends both 'status' and 'deal_status' fields
   * Both represent the same value: 0=inactive, 1=active
   */
  const getStatusValue = (deal: DealOfTheDay): number => {
    // Try deal_status first (from backend alias), then status
    const statusValue = deal.deal_status !== undefined ? deal.deal_status : deal.status;
    return statusValue === 1 ? 1 : 0;
  };

  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '00:00' };
    
    try {
      const dateObj = new Date(dateTimeString);
      const date = dateObj.toISOString().split('T')[0];
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;
      
      return { date, time };
    } catch (error) {
      console.error('Error parsing datetime:', error);
      return { date: '', time: '00:00' };
    }
  };

  const combineDateTime = (date: string, time: string): string => {
    if (!date) return '';
    const actualTime = time || '00:00';
    return `${date} ${actualTime}:00`;
  };

  // Enrich deals with product details
  useEffect(() => {
    if (deals.length > 0 && allProducts.length > 0) {
      const enriched = deals.map((deal: DealOfTheDay) => {
        const productId = typeof deal.product_id === 'string' ? parseInt(deal.product_id) : deal.product_id;
        const product = allProducts.find((p: Product) => 
          (p.product_id === productId || p.id === productId)
        );

        const originalPrice = product?.product_price || 0;
        const discountAmount = (originalPrice * deal.discount) / 100;
        const salePrice = originalPrice - discountAmount;

        return {
          ...deal,
          product_name: product?.prod_name || 'Unknown Product',
          product_image: product?.product_main_image || '',
          prod_name: product?.prod_name || '',
          product_main_image: product?.product_main_image || '',
          original_price: originalPrice,
          currency: product?.currency || 'AED',
          sale_price: salePrice,
          vendor_id: getVendorId(deal) ?? getVendorId(product),
          vendor_name: getVendorName(deal) ?? getVendorName(product),
        };
      });
      setEnrichedDeals(enriched);
    } else if (deals.length === 0) {
      setEnrichedDeals([]);
      setFilteredDeals([]);
    }
  }, [deals, allProducts]);

  // Filter deals based on search and tab
  useEffect(() => {
    if (enrichedDeals.length > 0) {
      let filtered = [...enrichedDeals];
      
      if (searchStr.trim()) {
        const searchLower = searchStr.toLowerCase();
        filtered = filtered.filter((deal: EnrichedDeal) => {
          return (
            (deal.product_name?.toLowerCase().includes(searchLower)) ||
            (deal.prod_name?.toLowerCase().includes(searchLower)) ||
            (deal.product_id?.toString().includes(searchLower)) ||
            (deal.deal_id?.toString().includes(searchLower)) ||
            (deal.discount?.toString().includes(searchLower)) ||
            (deal.original_price?.toString().includes(searchLower)) ||
            (deal.sale_price?.toString().includes(searchLower)) ||
            (deal.vendor_name?.toLowerCase().includes(searchLower)) ||
            (deal.vendor_id?.toString().includes(searchLower))
          );
        });
      }
      
      // Admin can narrow the list to one vendor using business name or vendor ID.
      if (vendorSearchStr.trim()) {
        const vendorQuery = vendorSearchStr.trim().toLowerCase();
        filtered = filtered.filter((deal: EnrichedDeal) =>
          deal.vendor_name?.toLowerCase().includes(vendorQuery) ||
          deal.vendor_id?.toString().includes(vendorQuery)
        );
      }

      // Apply tab filter based on status value
      if (tabValue === 1) { // Active tab
        filtered = filtered.filter((deal: EnrichedDeal) => getStatusValue(deal) === 1);
      } else if (tabValue === 2) { // Inactive tab
        filtered = filtered.filter((deal: EnrichedDeal) => getStatusValue(deal) === 0);
      }
      
      setFilteredDeals(filtered);
    } else {
      setFilteredDeals([]);
    }
  }, [enrichedDeals, searchStr, vendorSearchStr, tabValue]);

  useEffect(() => {
    setPage(1);
    // Reset scroll position when tab or search changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [tabValue, searchStr, vendorSearchStr]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    // Reset scroll position when page changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      status: 1,
      discount: '',
      start_date: '',
      end_date: '',
      start_time: '00:00',
      end_time: '23:59',
    });
    setSelectedProduct(null);
    setProductSearchStr('');
    setProductVendorSearchStr('');
    setFormErrors({});
  };

  const handleAddDeal = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditDeal = (deal: EnrichedDeal) => {
    const product = allProducts.find((p: Product) => 
      (p.product_id === deal.product_id || p.id === deal.product_id)
    );
    
    setSelectedProduct(product || null);
    
    const startDateTime = parseDateTime(deal.start_date);
    const endDateTime = parseDateTime(deal.end_date);
    
    // FIXED: Use getStatusValue to extract correct status
    const statusValue = getStatusValue(deal);
    
    console.log('Edit Deal - Raw Deal Data:', deal);
    console.log('Edit Deal - deal_id:', deal.deal_id);
    console.log('Edit Deal - status value:', statusValue);
    
    setFormData({
      deal_id: deal.deal_id,
      product_id: deal.product_id.toString(),
      status: statusValue,
      discount: deal.discount.toString(),
      start_date: startDateTime.date,
      end_date: endDateTime.date,
      start_time: startDateTime.time,
      end_time: endDateTime.time,
    });
    
    console.log('Edit Deal - Form Data Set:', {
      deal_id: deal.deal_id,
      product_id: deal.product_id.toString(),
      status: statusValue,
      discount: deal.discount.toString(),
      start_date: startDateTime.date,
      end_date: endDateTime.date,
      start_time: startDateTime.time,
      end_time: endDateTime.time,
    });
    
    setFormErrors({});
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof DealFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleProductSelect = (event: any, value: Product | null) => {
    setSelectedProduct(value);
    setFormData(prev => ({
      ...prev,
      product_id: value ? (value.product_id || value.id || '').toString() : ''
    }));
    if (formErrors.product_id) {
      setFormErrors(prev => ({
        ...prev,
        product_id: undefined
      }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked ? 1 : 0;
    console.log('Status toggle - New value:', newStatus);
    setFormData(prev => ({
      ...prev,
      status: newStatus
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof DealFormData, string>> = {};

    if (!formData.product_id || !formData.product_id.trim()) {
      errors.product_id = 'Product is required';
    }
    
    if (formData.discount && formData.discount.trim() !== '') {
      const discountValue = parseFloat(formData.discount);
      if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
        errors.discount = 'Discount must be between 0 and 100';
      }
    }
    
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    
    if (formData.start_date && formData.end_date) {
      const startDateTime = new Date(combineDateTime(formData.start_date, formData.start_time));
      const endDateTime = new Date(combineDateTime(formData.end_date, formData.end_time));
      
      if (endDateTime <= startDateTime) {
        errors.end_date = 'End date must be after start date';
      }
    }

    if (isEditing && !formData.deal_id) {
      console.error('Edit mode but no deal_id found:', formData);
      setSnackbar({
        open: true,
        message: 'Error: Deal ID is missing. Cannot update.',
        severity: 'error'
      });
      return false;
    }

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    
    if (!isValid) {
      console.error('Validation errors:', errors);
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    console.log('isEditing:', isEditing);
    
    if (!validateForm()) {
      console.error('Validation failed');
      return;
    }

    if (!selectedProduct && !isEditing) {
      setSnackbar({
        open: true,
        message: 'Please select a product',
        severity: 'error'
      });
      return;
    }

    try {
      const startDateTime = combineDateTime(formData.start_date, formData.start_time);
      const endDateTime = combineDateTime(formData.end_date, formData.end_time);
      
      if (isEditing && formData.deal_id) {
        const updatePayload = {
          deal_id: Number(formData.deal_id),
          product_id: Number(formData.product_id),
          start_date: startDateTime,
          end_date: endDateTime,
          discount: formData.discount && formData.discount.trim() !== '' ? Number(formData.discount) : 0,
          status: Number(formData.status), // This will correctly send 0 or 1
        };
        
        console.log('UPDATE - Sending payload:', updatePayload);
        console.log('UPDATE - Status value:', updatePayload.status, '(0=inactive, 1=active)');
        
        await updateDeal(updatePayload).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal updated successfully!',
          severity: 'success'
        });
      } else {
        const addPayload = {
          product_id: Number(formData.product_id),
          start_date: startDateTime,
          end_date: endDateTime,
          discount: formData.discount && formData.discount.trim() !== '' ? Number(formData.discount) : 0,
          status: Number(formData.status),
        };
        
        console.log('ADD - Sending payload:', addPayload);
        console.log('ADD - Status value:', addPayload.status, '(0=inactive, 1=active)');
        
        await addDeal(addPayload).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal added successfully!',
          severity: 'success'
        });
      }
      
      handleBackToList();
      refetch();
    } catch (err: any) {
      console.error('Failed to save deal:', err);
      console.error('Error response:', err?.data);
      setSnackbar({
        open: true,
        message: err?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} deal`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (deal: EnrichedDeal) => {
    const dealId = deal.deal_id;
    
    if (!dealId) {
      setSnackbar({
        open: true,
        message: 'Error: Deal ID is missing. Cannot delete.',
        severity: 'error'
      });
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete this deal? (ID: ${dealId})`)) {
      try {
        await deleteDeal(dealId).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete deal:', err);
        const errorMessage = err?.data?.message || err?.message || 'Failed to delete deal. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol'
    }).format(amount);
  };

  const getDisplayId = (deal: EnrichedDeal) => {
    return deal.deal_id || 'N/A';
  };

  /**
   * FIXED: Render status chip based on actual status value
   * status = 1 → Active (green)
   * status = 0 → Inactive (gray)
   */
  const renderStatus = (deal: EnrichedDeal) => {
    const statusValue = getStatusValue(deal);
    
    if (statusValue === 1) {
      return (
        <Chip
          label="Active"
          size="small"
          sx={{
            bgcolor: '#d1f4e0',
            color: '#00783e',
            fontWeight: 600,
            fontSize: '12px'
          }}
        />
      );
    }
   
    return (
      <Chip
        label="Inactive"
        size="small"
        sx={{
          bgcolor: '#e4e5e7',
          color: '#616161',
          fontWeight: 600,
          fontSize: '12px'
        }}
      />
    );
  };

  // Calculate counts for tabs
  const activeDealsCount = enrichedDeals.filter(deal => getStatusValue(deal) === 1).length;
  const inactiveDealsCount = enrichedDeals.filter(deal => getStatusValue(deal) === 0).length;

  const isLoadingData = isLoading || isLoadingAllProducts;

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        times.push(`${hourStr}:${minuteStr}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Deals of the Day
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddDeal}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add deal
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={handleTabChange}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${enrichedDeals.length})`} />
                      <Tab label={`Active (${activeDealsCount})`} />
                      <Tab label={`Inactive (${inactiveDealsCount})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search product name, product ID, deal ID..."
                      value={searchStr}
                      onChange={handleSearch}
                      sx={{
                        flex: 1,
                        minWidth: 260,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      size="small"
                      placeholder="Filter vendor by name or ID..."
                      value={vendorSearchStr}
                      onChange={(e) => { setVendorSearchStr(e.target.value); setPage(1); }}
                      sx={{
                        minWidth: 260,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterList fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {isLoadingData && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoadingData && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load deals. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoadingData && !isError && paginatedDeals.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <LocalOffer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No deals found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first deal'}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddDeal}
                        sx={{
                          bgcolor: '#202223',
                          color: 'white',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#303030' }
                        }}
                      >
                        Add deal
                      </Button>
                    </Box>
                  )}

                  {!isLoadingData && paginatedDeals.length > 0 && (
                    <>
                      {/* Scroll Navigation Bar */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                      }}>
                        <IconButton
                          size="small"
                          onClick={handleScrollLeft}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronLeft fontSize="small" />
                        </IconButton>

                        <Box sx={{
                          flex: 1,
                          height: '4px',
                          mx: 1,
                          bgcolor: '#e1e3e5',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: '30%',
                            bgcolor: '#6d7175',
                            borderRadius: '2px',
                            opacity: 0.4,
                          }} />
                        </Box>

                        <IconButton
                          size="small"
                          onClick={handleScrollRight}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Scrollable Table Container */}
                      <Box 
                        ref={scrollContainerRef}
                        sx={{ 
                          bgcolor: 'white', 
                          overflowX: 'auto',
                          '&::-webkit-scrollbar': {
                            height: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f2f4',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#c1c4c8',
                            borderRadius: '4px',
                            '&:hover': {
                              background: '#a1a4a8',
                            },
                          },
                        }}
                      >
                        <Box sx={{ minWidth: '1400px' }}>
                          {/* Table Header */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '50px 100px 1fr 120px 100px 120px 180px 180px 100px 100px',
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #e1e3e5',
                            bgcolor: '#fafbfb'
                          }}>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              #
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Deal ID
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Product
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Original
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Discount
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Sale Price
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Start Date
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              End Date
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                              Status
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                              Actions
                            </Typography>
                          </Box>

                          {/* Table Rows */}
                          {paginatedDeals.map((deal: EnrichedDeal, index: number) => (
                            <Box
                              key={deal.deal_id || index}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 100px 1fr 120px 100px 120px 180px 180px 100px 100px',
                                gap: 2,
                                px: 2,
                                py: 2,
                                alignItems: 'center',
                                borderBottom: index < paginatedDeals.length - 1 ? '1px solid #e1e3e5' : 'none',
                                '&:hover': { bgcolor: '#f9fafb' },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#6d7175' }}>
                                {(page - 1) * dealsPerPage + index + 1}
                              </Typography>

                              <Chip 
                                label={getDisplayId(deal)} 
                                size="small"
                                sx={{ 
                                  bgcolor: '#fff3e0',
                                  color: '#e65100',
                                  fontWeight: 600,
                                  fontSize: '11px'
                                }}
                              />

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  src={deal.product_image || deal.product_main_image ? `https://fftestservice.sarsetech.com/images/${deal.product_image || deal.product_main_image}` : undefined}
                                  variant="rounded"
                                  sx={{ width: 40, height: 40, bgcolor: '#f1f2f4' }}
                                >
                                  {(deal.product_name || deal.prod_name)?.[0] || 'P'}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                    {deal.product_name || deal.prod_name || 'Product'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {deal.product_id}
                                  </Typography>
                                  {deal.vendor_id !== null && deal.vendor_id !== undefined ? (
                                    <>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Vendor: {deal.vendor_name || 'Vendor'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Vendor ID: {deal.vendor_id}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      Vendor: Fortune Five (Own Product)
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                {formatCurrency(deal.original_price || 0, deal.currency)}
                              </Typography>

                              <Chip
                                label={`${deal.discount}%`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#fff3e0',
                                  color: '#e65100',
                                  fontWeight: 600
                                }}
                              />

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                {formatCurrency(deal.sale_price || 0, deal.currency)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDateTime(deal.start_date)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDateTime(deal.end_date)}
                              </Typography>

                              <Box>
                                {renderStatus(deal)}
                              </Box>

                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditDeal(deal)}
                                  sx={{ 
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' }
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(deal)}
                                  disabled={isDeleting}
                                  sx={{ 
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </>
                  )}
                </Card>

                {totalPages > 1 && !isLoadingData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Deals of the Day
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit deal' : 'Add deal'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Deal details
                            </Typography>
                            
                            <TextField
                              size="small"
                              label="Search Vendor by Name or ID"
                              placeholder="Enter vendor name or vendor ID"
                              value={productVendorSearchStr}
                              onChange={(e) => {
                                setProductVendorSearchStr(e.target.value);
                                // Clear an already selected product if it no longer belongs to the searched vendor.
                                if (selectedProduct && e.target.value.trim()) {
                                  const query = e.target.value.trim().toLowerCase();
                                  const selectedVendorName = getVendorName(selectedProduct)?.toLowerCase() || '';
                                  const selectedVendorId = getVendorId(selectedProduct)?.toString().toLowerCase() || '';
                                  if (!selectedVendorName.includes(query) && !selectedVendorId.includes(query)) {
                                    setSelectedProduct(null);
                                    setFormData(prev => ({ ...prev, product_id: '' }));
                                  }
                                }
                              }}
                              fullWidth
                              sx={{ mb: 1.5 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <FilterList fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Product Search & Select */}
                            <Autocomplete
                              options={productOptions}
                              getOptionLabel={(option: Product) => `${option.prod_name || 'Product'} (ID: ${option.product_id || option.id})${getVendorId(option) !== null ? ` • Vendor: ${getVendorName(option) || 'Vendor'} (ID: ${getVendorId(option)})` : ' • Vendor: Fortune Five (Own Product)'}`}
                              value={selectedProduct}
                              onChange={handleProductSelect}
                              onInputChange={(event, newInputValue) => {
                                setProductSearchStr(newInputValue);
                              }}
                              loading={isLoadingProducts}
                              disabled={isEditing}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Search & Select Product *"
                                  error={!!formErrors.product_id}
                                  helperText={formErrors.product_id || (isEditing ? 'Product cannot be changed in edit mode' : 'Type to search for products')}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        <InputAdornment position="start">
                                          <Search />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                      </>
                                    ),
                                    endAdornment: (
                                      <>
                                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              renderOption={(props, option: Product) => (
                                <Box component="li" {...props} key={option.product_id || option.id}>
                                  <Avatar
                                    src={option.product_main_image ? `https://fftestservice.sarsetech.com/images/${option.product_main_image}` : undefined}
                                    variant="rounded"
                                    sx={{ width: 40, height: 40, mr: 2 }}
                                  >
                                    {option.prod_short_name?.[0] || option.prod_name?.[0] || 'P'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {option.prod_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Product ID: {option.product_id || option.id} | Price: {formatCurrency(option.product_price || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {getVendorId(option) !== null ? `Vendor: ${getVendorName(option) || 'Vendor'} (ID: ${getVendorId(option)})` : 'Vendor: Fortune Five (Own Product)'}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              noOptionsText={
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {productVendorSearchStr.trim()
                                      ? `No products found for vendor "${productVendorSearchStr.trim()}".`
                                      : 'No products found. Try a different search term.'}
                                  </Typography>
                                </Box>
                              }
                            />

                            {selectedProduct && (
                              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '8px', mt: 2, border: '1px solid #e1e3e5' }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                                  SELECTED PRODUCT:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                  <Avatar
                                    src={selectedProduct.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedProduct.product_main_image}` : undefined}
                                    variant="rounded"
                                    sx={{ width: 60, height: 60 }}
                                  >
                                    {selectedProduct.prod_short_name?.[0] || selectedProduct.prod_name?.[0] || 'P'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {selectedProduct.prod_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Product ID: {selectedProduct.product_id || selectedProduct.id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {getVendorId(selectedProduct) !== null ? `Vendor: ${getVendorName(selectedProduct) || 'Vendor'} (ID: ${getVendorId(selectedProduct)})` : 'Vendor: Fortune Five (Own Product)'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Original Price: {formatCurrency(selectedProduct.product_price || 0)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            <TextField
                              label="Discount (%)"
                              name="discount"
                              type="number"
                              fullWidth
                              value={formData.discount}
                              onChange={handleInputChange}
                              error={!!formErrors.discount}
                              helperText={formErrors.discount || 'Optional. Enter discount percentage (0-100) or leave empty'}
                              sx={{ mt: 2 }}
                              InputProps={{
                                inputProps: { 
                                  min: 0, 
                                  max: 100, 
                                  step: 1 
                                }
                              }}
                            />

                            {selectedProduct && formData.discount && formData.discount.trim() !== '' && parseFloat(formData.discount) > 0 && (
                              <Box sx={{ p: 2, bgcolor: '#f1f8e9', borderRadius: '8px', mt: 2, border: '1px solid #c8e6c9' }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                                  PRICE PREVIEW:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Original Price:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {formatCurrency(selectedProduct.product_price || 0)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h6" color="text.secondary">→</Typography>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Sale Price:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                      {formatCurrency(
                                        (selectedProduct.product_price || 0) - 
                                        ((selectedProduct.product_price || 0) * parseFloat(formData.discount) / 100)
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Date & Time
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Start Date *"
                                  name="start_date"
                                  type="date"
                                  fullWidth
                                  value={formData.start_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.start_date}
                                  helperText={formErrors.start_date}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  inputProps={{
                                    max: formData.end_date || '2100-12-31'
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  label="Start Time"
                                  name="start_time"
                                  value={formData.start_time}
                                  onChange={handleInputChange}
                                  fullWidth
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {time}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <FormHelperText>Default: 00:00 (Start of day)</FormHelperText>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="End Date *"
                                  name="end_date"
                                  type="date"
                                  fullWidth
                                  value={formData.end_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.end_date}
                                  helperText={formErrors.end_date}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  inputProps={{
                                    min: formData.start_date || '1900-01-01'
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  label="End Time"
                                  name="end_time"
                                  value={formData.end_time}
                                  onChange={handleInputChange}
                                  fullWidth
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {time}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <FormHelperText>Default: 23:59 (End of day)</FormHelperText>
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.status === 1}
                                onChange={handleStatusChange}
                                color="primary"
                              />
                            }
                            label={formData.status === 1 ? "Active" : "Inactive"}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {formData.status === 1 
                              ? "Deal is active and visible to users" 
                              : "Deal is inactive and hidden from users"}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                      <Button
                      onClick={handleBackToList}
                      disabled={isSaving}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#f44336',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleBackToList}
                      disabled={isSaving}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#F78E12',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSaving || !selectedProduct || !formData.start_date || !formData.end_date}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isSaving ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </Box>
                      ) : (
                        isEditing ? 'Update deal' : 'Add deal'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DealOfTheDay;